import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { CustomPagination } from 'utils/functions';
import localeText from 'utils/localeText';
import moment from 'moment';
import styles from '../styles';

const FormaçãoDoCusto = ({ indicadores: produto }) => {
  const resumo = produto?.indicadores?.formacaoCusto?.resumo || {};
  const reposicao = produto?.indicadores?.formacaoCusto?.reposicao || {};
  const is_produto_fabricado = Boolean(reposicao?.dados?.ordem_fabricacao);

  const DadosReposicao = ({ dados }) => {
    const arr = [
      { label: 'Documento', value: dados?.documento },
      {
        label: 'Data de Entrada',
        value:
          dados?.dtentrada && moment(dados?.dtentrada).format('DD/MM/YYYY'),
      },
      { label: 'Fornecedor', value: dados?.fornecedor },
      { label: 'Usuário', value: dados?.usuario },
      { label: 'Moeda', value: dados?.moeda },
      {
        label: 'Cotação da Moeda',
        value:
          dados?.moeda_cotacao &&
          dados?.moeda_cotacao.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
          }),
      },
      {
        label: 'Cotação da Moeda Atual',
        value:
          dados?.moeda_cotacao_atual &&
          dados?.moeda_cotacao_atual.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
          }),
      },
      { label: 'Ordem de Fabricação', value: dados?.ordem_fabricacao },
      { label: 'Unidade de Medida', value: dados?.unidade_medida },
      {
        label: 'Quantidade Fornecida',
        value: `${dados?.quantidade_fornecida || 0} ${dados?.unidade_medida || ''}`,
      },
      {
        label: 'Quantidade em Estoque',
        value: `${dados?.quantidade_estoque || 0} ${dados?.unidade_medida || ''}`,
      },
    ];
    const expanded = arr?.length > 0 ? undefined : false;
    const hideIcon = arr?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded} defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Typography variant="body1" fontWeight="bold">
            Dados
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {arr?.map(({ label, value }, i) => (
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Typography variant="caption" fontWeight="bold" flex={1}>
                {label}:
              </Typography>
              <Typography variant="caption" flex={4}>
                {value || '-'}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const Detalhes = ({ detalhes = {}, total = 0 }) => {
    const arr = Object.keys(detalhes)?.map((k) => ({
      label: k,
      value: detalhes[k] || 0,
    }));
    const expanded = arr?.length > 0 ? undefined : false;
    const hideIcon = arr?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Detalhes
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {arr?.map(({ label, value }, i) => (
            <Box key={i} gap={0.5} display="flex" alignItems="flex-end">
              <Typography variant="caption">{label}</Typography>
              <Box flex={1} borderBottom="1px dotted" mb={0.5} />
              <Typography variant="caption">
                {value?.toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const Tributos = ({ tributos = [], total = 0 }) => {
    const expanded = tributos?.length > 0 ? undefined : false;
    const hideIcon = tributos?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Tributos
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {tributos?.map((t, i) => (
            <Box key={i} gap={0.5} display="flex" alignItems="flex-end">
              <Typography variant="caption">{t?.tributo}</Typography>
              <Box flex={1} borderBottom="1px dotted" mb={0.5} />
              <Typography variant="caption">
                {(t?.valor || 0)?.toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </Typography>
              <Typography variant="caption">
                (
                {(t?.percentual || 0)?.toLocaleString('pt-br', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}
                %)
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const Materiais = ({ materiais = [], total = 0 }) => {
    const expanded = materiais?.length > 0 ? undefined : false;
    const hideIcon = materiais?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Materiais
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid
            rows={materiais}
            columns={[
              {
                field: 'material',
                headerName: 'Material',
                minWidth: 250,
                flex: 1,
                sortable: false,
              },
              {
                field: 'quantidade',
                headerName: 'Quantidade',
                width: 100,
                sortable: false,
                type: 'number',
              },
              {
                field: 'perda',
                headerName: 'Perda',
                width: 100,
                sortable: false,
                type: 'number',
              },
              {
                field: 'custo_unitario',
                headerName: 'Custo Unitário',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  (value || 0)?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
              {
                field: 'custo_total',
                headerName: 'Custo Total',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  (value || 0)?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
              {
                field: 'data_compra',
                headerName: 'Data da Compra',
                width: 150,
                sortable: false,
                valueGetter: ({ value }) =>
                  value && moment(value).format('DD/MM/YYYY'),
              },
            ]}
            autoHeight
            pagination
            pageSize={10}
            density="compact"
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            disableSelectionOnClick
            localeText={localeText}
            getRowHeight={() => 'auto'}
            components={{ Pagination: CustomPagination }}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const Processos = ({ processos = [], total = 0 }) => {
    const expanded = processos?.length > 0 ? undefined : false;
    const hideIcon = processos?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Processos
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid
            rows={processos}
            columns={[
              {
                field: 'descricao',
                headerName: 'Descrição',
                minWidth: 250,
                flex: 1,
                sortable: false,
              },
              {
                field: 'operacao',
                headerName: 'Operação',
                width: 100,
                sortable: false,
                type: 'number',
              },
              {
                field: 'ccusto',
                headerName: 'Centro de Custo',
                minWidth: 250,
                flex: 1,
                sortable: false,
              },
              {
                field: 'tempo_previsto',
                headerName: 'Tempo Previsto',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) => {
                  const duration = moment.duration(value, 'minutes');
                  const horas = Math.floor(duration.asHours());
                  const mins = duration.minutes();
                  const secs = duration.seconds();

                  return `${horas}h: ${mins}m: ${secs}s`;
                },
              },
              {
                field: 'tempo_apontado',
                headerName: 'Tempo Apontado',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) => {
                  const duration = moment.duration(value, 'minutes');
                  const horas = Math.floor(duration.asHours());
                  const mins = duration.minutes();
                  const secs = duration.seconds();

                  return `${horas}h: ${mins}m: ${secs}s`;
                },
              },
              {
                field: 'valor',
                headerName: 'Valor Total',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  (value || 0)?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
            ]}
            autoHeight
            pagination
            pageSize={10}
            density="compact"
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            disableSelectionOnClick
            localeText={localeText}
            getRowHeight={() => 'auto'}
            components={{ Pagination: CustomPagination }}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} textAlign="center">
        <Typography variant="h3" fontWeight="bold" color="primary">
          {resumo?.valor_reposicao?.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
          })}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DadosReposicao dados={reposicao?.dados} />
        {!is_produto_fabricado && (
          <Detalhes
            detalhes={reposicao?.detalhes}
            total={reposicao?.total_detalhes}
          />
        )}
        {!is_produto_fabricado && (
          <Tributos
            tributos={reposicao?.tributos}
            total={reposicao?.total_tributos}
          />
        )}
        {is_produto_fabricado && (
          <Materiais
            materiais={reposicao?.materiais}
            total={reposicao?.total_materiais}
          />
        )}
        {is_produto_fabricado && (
          <Processos
            processos={reposicao?.processos}
            total={reposicao?.total_processos}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default FormaçãoDoCusto;
